import { useEffect, useRef } from "react";
import { useRefreshTokenMutation } from "../../redux/apis/Authentication/authApi";
import { selectAuth, useAppSelector } from "../store";

/** Fire refresh this many ms before access token expiry. */
const REFRESH_BEFORE_EXPIRY_MS = 30_000;

/** If the user has been inactive longer than this when refresh fires, skip refresh (session ends at expiry). */
const MAX_IDLE_MS_BEFORE_REFRESH = 30_000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

/**
 * Schedules silent POST /getrefreshtoken ~30s before expiry when the user was recently active.
 * Idle users are not refreshed and are logged out by {@link AuthSessionWatcher} at expiry.
 *
 * Uses {@link useRefreshTokenMutation} (RTK Query only generates lazy-style triggers for mutations via this hook).
 */
export function BackgroundTokenRefresh() {
  const { accessToken, tokenExpiresAtMs } = useAppSelector(selectAuth);
  const [triggerRefresh] = useRefreshTokenMutation();

  const lastActivityRef = useRef(Date.now());
  const tokenRef = useRef(accessToken);
  const expiresRef = useRef(tokenExpiresAtMs);
  const refreshInFlightRef = useRef(false);

  tokenRef.current = accessToken;
  expiresRef.current = tokenExpiresAtMs;

  useEffect(() => {
    if (accessToken) {
      lastActivityRef.current = Date.now();
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }

    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, onActivity, { passive: true, capture: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, onActivity, true);
      });
    };
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || tokenExpiresAtMs == null) {
      return undefined;
    }

    const scheduleRefresh = () => {
      const expiresAt = expiresRef.current;
      if (expiresAt == null) return;

      const delay = expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
      return Math.max(0, delay);
    };

    let timeoutId;

    const run = async () => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= MAX_IDLE_MS_BEFORE_REFRESH) {
        return;
      }

      const current = tokenRef.current;
      if (!current || refreshInFlightRef.current) {
        return;
      }

      refreshInFlightRef.current = true;
      try {
        await triggerRefresh({ oldToken: current }).unwrap();
      } catch {
        /* Let expiry / next login handle failure */
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const delay = scheduleRefresh();
    if (delay === undefined) {
      return undefined;
    }

    timeoutId = window.setTimeout(run, delay);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [accessToken, tokenExpiresAtMs, triggerRefresh]);

  return null;
}
